import { cn } from '@hrms/ui';

export interface CourseProgressRingProps {
  /** 0–100; clamped, and a non-finite value reads as 0. */
  percent: number;
  /** Outer diameter in px. The percentage is printed inside from 44px up. */
  size?: number;
  /** Screen-reader name, e.g. "POSH Awareness progress". Defaults to "Course progress". */
  label?: string;
  className?: string;
}

/**
 * The course-progress ring in the player's top bar and on the "Continue learning" card.
 *
 * Deliberately not `@hrms/ui`'s `Ring`: that one is a dashboard centrepiece (gradient arc, glow,
 * clock ticks, draw-in animation). This is a quiet 32–64px indicator in the learning module's own
 * progress tokens (`--progress-track` / `--progress-fill`), so the ring, the thin bars on course
 * cards and the ticks in the lesson list all speak the same colour (PLAN §10 "Progress language").
 */
export function CourseProgressRing({
  percent,
  size = 40,
  label = 'Course progress',
  className,
}: CourseProgressRingProps) {
  const clamped = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  const rounded = Math.round(clamped);
  const strokeWidth = Math.max(3, Math.round(size / 10));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={rounded}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`${rounded}% complete`}
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        className="-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--progress-track)"
          strokeWidth={strokeWidth}
        />
        {rounded > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--progress-fill)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped / 100)}
            className="transition-[stroke-dashoffset] duration-500 ease-out"
          />
        )}
      </svg>
      {size >= 44 && (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-xs font-semibold tabular-nums text-fg"
        >
          {rounded}%
        </span>
      )}
    </div>
  );
}
